import React from 'react';
import { IQuestionnaire } from '../../../modules/unsw-fhir/Questionnaire';
import { usePage } from '../../../modules/unsw-wizard/Contexts/PageContext';
import { WizardPage } from '../../../modules/unsw-wizard';
import { IQuestionnaireResponse } from '../../../modules/unsw-fhir/QuestionnaireResponse';

import './index.css';
import Questionnaire from '../Questionnaire';
import QuestionnaireProvider from '../../Providers/Questionnaire';
import {
  useQuestionnaire,
  QuestionnaireContext,
} from '../../../contexts/questionnaire';
import { useQuestionnaireReponse } from '../../../contexts/questionnaireResponse';
import QuestionnaireResponseProvider from '../../Providers/QuestionnnaireResponse';
import { usePractitioner } from '../../../contexts/practitioner';
import { usePatient } from '../../../contexts/patient';

const WizardQuestionnaireRender = ({
  beforeSave,
  onComplete,
}: {
  loading?: boolean;
  prefill?: (
    response: IQuestionnaireResponse | undefined,
  ) => Promise<IQuestionnaireResponse> | IQuestionnaireResponse;
  beforeSave?: (
    response: IQuestionnaireResponse | undefined,
    questionnaire: IQuestionnaire,
  ) =>
    | Promise<IQuestionnaireResponse | undefined>
    | IQuestionnaireResponse
    | undefined;
  onComplete?: (
    questionnaire: IQuestionnaire,
    questionnaireResponse?: IQuestionnaireResponse,
  ) => void | Promise<void>;
}) => {
  const { onComplete: onPageComplete } = usePage();
  const { questionnaire } = useQuestionnaire();
  const {
    questionnaireResponse,
    setQuestionnaireResponse,
    questionnaireResponseLoading,
  } = useQuestionnaireReponse();
  const { practitioner } = usePractitioner();
  if (!practitioner || !practitioner.id) return <></>;
  return !questionnaireResponseLoading ? (
    <Questionnaire
      beforeSave={beforeSave}
      authorId={practitioner.id}
      questionnaire={questionnaire}
      questionnaireResponse={questionnaireResponse}
      onComplete={async (questionnaire, response) => {
        if (onComplete) await onComplete(questionnaire, response);
        onPageComplete();
      }}
      setQuestionnaireResponse={setQuestionnaireResponse}
      loading={questionnaireResponseLoading}
    />
  ) : null;
};

export type WizardQuestionnaireProps = {
  pageId: string;
  name?: string;
  questionnaireId?: string;
  disabled?: boolean;
  prefill?: (
    response: IQuestionnaireResponse | undefined,
  ) => Promise<IQuestionnaireResponse> | IQuestionnaireResponse;
  beforeSave?: (
    response: IQuestionnaireResponse | undefined,
    questionnaire: IQuestionnaire,
  ) =>
    | Promise<IQuestionnaireResponse | undefined>
    | IQuestionnaireResponse
    | undefined;
  onComplete?: (
    questionnaire: IQuestionnaire,
    questionnaireResponse?: IQuestionnaireResponse,
  ) => void | Promise<void>;
};

const WizardQuestionnaire = ({
  name,
  pageId,
  questionnaireId,
  disabled,
  beforeSave,
  onComplete,
}: WizardQuestionnaireProps) => {
  // get Questionnaire
  const { patient } = usePatient();
  const patientId = patient ? patient.id : undefined;
  return (
    <QuestionnaireProvider
      quesionnaireName={name}
      questionnaireId={questionnaireId}>
      <QuestionnaireContext.Consumer>
        {({ questionnaire, loading }) => (
          <QuestionnaireResponseProvider
            questionnaireId={questionnaire && questionnaire.id}
            patientId={patientId}>
            <WizardPage
              name={pageId}
              title={questionnaire ? questionnaire.title || '' : ''}
              subTitle={questionnaire ? questionnaire.description : ''}
              disabled={disabled}
              loading={loading}>
              <WizardQuestionnaireRender
                beforeSave={beforeSave}
                onComplete={onComplete}
                loading={loading}
              />
            </WizardPage>
          </QuestionnaireResponseProvider>
        )}
      </QuestionnaireContext.Consumer>
    </QuestionnaireProvider>
  );
};

export default WizardQuestionnaire;
